// src/services/dataSearchService.js
// *******************************************************************************
// DATA SEARCH SERVICE
// *******************************************************************************
// Service for managing data search results (dataSearches collection).
// Provides methods for linking searches to entities and updating sync status.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************

import axiosService from './axiosService';
import { API } from 'constants/Api';

// *******************************************************************************
// TRANSFORM HELPERS
// *******************************************************************************

/**
 * Transform addresses from dataSearch format to entities format
 * Ensures addressMetadata.countryCode is always set (required by schema)
 *
 * @param {Array} addresses - Addresses from dataSearch
 * @param {Object} enrichmentData - Enrichment data (for geolocation)
 * @returns {Array} Transformed addresses for entities
 */
function transformAddressesForEntity(addresses = [], enrichmentData = {}) {
  return addresses.map(address => {
    const countryCode = address.countryCode || 'BR';
    const addressData = {
      addressType: Array.isArray(address.addressType)
        ? address.addressType
        : [address.addressType || 'main'],
      zip: address.zip,
      address1: address.address1,
      number: address.number,
      address2: address.address2,
      neighborhood: address.neighborhood,
      city: address.city,
      cityCode: address.cityCode,
      region: address.region,
      countryCode: countryCode,
      isPrimary: address.isPrimary || false,
      // addressMetadata is required by schema with countryCode
      addressMetadata: {
        countryCode: countryCode,
        foundByApi: true,
        manual: false,
      },
      // addressAdditionalInfo for IBGE code
      ...(address.cityCode && {
        addressAdditionalInfo: {
          ibgeCode: address.cityCode,
        },
      }),
    };

    // Add geolocation if available
    if (enrichmentData.geolocation?.success && enrichmentData.geolocation?.data) {
      addressData.addressCoordinates = [
        enrichmentData.geolocation.data.latitude,
        enrichmentData.geolocation.data.longitude,
      ];
    }

    return addressData;
  });
}

/**
 * Transform contacts from dataSearch format to entities format
 * Filters out fax contacts and enriches with WhatsApp status
 *
 * @param {Array} contacts - Contacts from dataSearch
 * @param {Object} enrichmentData - Enrichment data (for WhatsApp)
 * @returns {Array} Transformed contacts for entities
 */
function transformContactsForEntity(contacts = [], enrichmentData = {}) {
  // Build WhatsApp status map from enrichment data
  const whatsappStatusMap = {};
  if (enrichmentData.whatsapp?.success && enrichmentData.whatsapp?.data?.numbers) {
    enrichmentData.whatsapp.data.numbers.forEach(n => {
      // Normalize phone number for matching (remove country code prefix)
      const cleanPhone = n.phoneNumber?.replace(/^55/, '');
      if (cleanPhone) {
        whatsappStatusMap[cleanPhone] = n.hasWhatsapp;
      }
    });
  }

  // Filter out fax contacts and enrich with WhatsApp status
  return contacts
    .filter(c => c.contactType !== 'fax')
    .map(c => {
      const hasWhatsApp = c.contactType === 'phone' ? (whatsappStatusMap[c.value] || false) : false;
      return {
        contactType: c.contactType,
        value: c.value,
        phoneType: c.phoneType,
        isPrimary: c.isPrimary || false,
        countryCode: c.countryCode || 'BR',
        hasWhatsApp,
      };
    });
}

/**
 * Build dataEnrichment object from enrichment data
 *
 * @param {Object} entityData - Entity data with provider info
 * @param {Object} enrichmentData - Enrichment data
 * @returns {Object} DataEnrichment object for entity
 */
function buildDataEnrichment(entityData = {}, enrichmentData = {}) {
  const now = new Date().toISOString();

  return {
    isEnriched: true,
    lastEnrichedAt: now,
    // CNPJ enrichment (always true since we got data from search)
    cnpj: {
      isEnriched: true,
      enrichedAt: now,
      provider: entityData.provider || 'jarvisly',
      status: 'found',
    },
    // CEP enrichment
    ...(enrichmentData.cep?.success && {
      cep: {
        isEnriched: true,
        enrichedAt: now,
        provider: enrichmentData.cep.provider || 'viacep',
        status: 'found',
      },
    }),
    // Geolocation enrichment
    ...(enrichmentData.geolocation?.success && {
      geolocation: {
        isEnriched: true,
        enrichedAt: now,
        provider: enrichmentData.geolocation.provider || 'google',
        status: 'found',
      },
    }),
    // WhatsApp enrichment
    ...(enrichmentData.whatsapp?.success && {
      whatsapp: {
        isEnriched: true,
        enrichedAt: now,
        provider: enrichmentData.whatsapp.provider || 'evolution-api',
        status: 'found',
      },
    }),
  };
}

/**
 * Build entity update payload from dataSearch data
 * Used when updating an existing entity with new API data
 *
 * @param {Object} searchData - Data from dataSearch (cnpjData or cpfData)
 * @returns {Object} Payload ready for entity update
 */
function buildEntityUpdatePayload(searchData) {
  if (!searchData) return {};

  const enrichmentData = searchData._enrichment || {};
  const updatePayload = {};

  // Basic fields - only update if new data has value
  const fieldsToUpdate = ['name', 'fullName', 'foundedAt', 'shareCapital', 'segment'];
  fieldsToUpdate.forEach(field => {
    if (searchData[field] !== undefined && searchData[field] !== null) {
      updatePayload[field] = searchData[field];
    }
  });

  // Handle legalNature (convert string to object if needed)
  if (searchData.legalNature) {
    if (typeof searchData.legalNature === 'string') {
      updatePayload.legalNature = {
        code: searchData.legalNatureCode || null,
        description: searchData.legalNature,
      };
    } else {
      updatePayload.legalNature = searchData.legalNature;
    }
  }

  // Handle companySize (convert string to object if needed)
  // Check both 'size' (legacy) and 'companySize' (normalized)
  const sizeValue = searchData.companySizeRaw || searchData.size;
  const sizeCode = searchData.companySizeCode || searchData.sizeCode;
  if (sizeValue) {
    updatePayload.companySize = {
      code: sizeCode?.toString() || null,
      description: sizeValue,
    };
  }

  // Handle registrationStatus (use normalized fields with fallback to legacy)
  if (searchData.registrationStatus) {
    const rs = searchData.registrationStatus;
    updatePayload.registrationStatus = {
      code: rs.statusCode?.toString() || rs.status?.toString() || rs.code,
      description: rs.statusRaw || rs.statusText || rs.description,
      date: rs.date,
    };
  }

  // Handle mainActivity (primary CNAE)
  if (searchData.activities?.length > 0) {
    const primary = searchData.activities.find(a => a.isPrimary) || searchData.activities[0];
    if (primary) {
      updatePayload.mainActivity = {
        code: primary.code,
        description: primary.description,
      };
    }

    // Handle additionalActivities (secondary CNAEs)
    const secondary = searchData.activities.filter(a => !a.isPrimary);
    if (secondary.length > 0) {
      updatePayload.additionalActivities = secondary.map(a => ({
        code: a.code,
        description: a.description,
      }));
    }
  }

  // Handle holders (partners/shareholders) - use normalized fields with fallback
  if (searchData.holders?.length > 0) {
    updatePayload.holders = searchData.holders.map(h => ({
      name: h.name,
      cpf: h.cpf,
      // Use normalized role/qualification with fallback to legacy
      qualification: h.roleRaw || h.qualification,
      sinceAt: h.sinceAt,
      // Use normalized holderType with fallback to legacy type
      legal: (h.holderType === 'company') ||
             (h.holderTypeRaw === 'Pessoa Jurídica') ||
             (h.type === 'Pessoa Jurídica'),
      ageRange: h.ageRangeRaw || h.ageRange,
    }));
  }

  // Handle fiscalData (use normalized fields with fallback)
  if (searchData.fiscalData) {
    const fd = searchData.fiscalData;
    // Determine taxRegimeCode based on normalized taxRegime or flags
    let taxRegimeCode = 3; // Default: lucro_presumido
    if (fd.taxRegime === 'simples_nacional' || fd.isSimples) taxRegimeCode = 1;
    else if (fd.taxRegime === 'mei' || fd.isMei) taxRegimeCode = 4;
    else if (fd.taxRegime === 'lucro_real') taxRegimeCode = 2;

    updatePayload.fiscalData = {
      mainCnae: fd.mainCnae,
      taxRegime: fd.taxRegimeRaw || fd.taxRegime,
      taxRegimeCode: taxRegimeCode,
      isSimples: fd.isSimples,
      simplesOptedAt: fd.simplesOptedAt,
      simplesExcludedAt: fd.simplesExcludedAt,
      isMei: fd.isMei,
      meiOptedAt: fd.meiOptedAt,
      meiExcludedAt: fd.meiExcludedAt,
    };
  }

  // Handle stateRegistrations (IE)
  if (searchData.stateRegistrations?.length > 0) {
    const primaryIE = searchData.stateRegistrations.find(ie => ie.isActive) || searchData.stateRegistrations[0];
    if (primaryIE?.stateRegistration) {
      updatePayload.fiscalData = {
        ...updatePayload.fiscalData,
        stateRegistration: primaryIE.stateRegistration,
      };
    }
  }

  // Build dataEnrichment
  updatePayload.dataEnrichment = buildDataEnrichment(searchData, enrichmentData);

  // Transform contacts and addresses with proper structure
  const validContacts = transformContactsForEntity(searchData.contacts, enrichmentData);
  const addresses = transformAddressesForEntity(searchData.addresses, enrichmentData);

  // Include contacts and addresses in payload for smart merge on backend
  if (validContacts.length > 0) {
    updatePayload._mergeContacts = validContacts;
  }
  if (addresses.length > 0) {
    updatePayload._mergeAddresses = addresses;
  }

  return updatePayload;
}

/**
 * Build entity create payload from dataSearch data
 * Used when creating a new entity from search data
 *
 * @param {Object} searchData - Data from dataSearch (cnpjData or cpfData)
 * @param {Array} entityProfile - Array of entity profiles to assign
 * @returns {Object} Payload ready for entity creation
 */
function buildEntityCreatePayload(searchData, entityProfile = []) {
  if (!searchData) return {};

  const enrichmentData = searchData._enrichment || {};

  // Get primary document for uniqueKey (required field)
  const cnpjDoc = searchData.documents?.find(d => d.documentType === 'cnpj');
  const cpfDoc = searchData.documents?.find(d => d.documentType === 'cpf');
  const uniqueKey = cnpjDoc?.value || cpfDoc?.value || '';

  // Convert legalNature from string to object { code, description }
  let legalNatureObj = null;
  if (searchData.legalNature) {
    if (typeof searchData.legalNature === 'string') {
      legalNatureObj = {
        code: searchData.legalNatureCode || null,
        description: searchData.legalNature,
      };
    } else {
      legalNatureObj = searchData.legalNature;
    }
  }

  // Convert size from string to object { code, description }
  // Check both 'size' (legacy) and 'companySize' (normalized)
  let companySizeObj = null;
  const sizeValue = searchData.companySizeRaw || searchData.size;
  const sizeCode = searchData.companySizeCode || searchData.sizeCode;
  if (sizeValue) {
    companySizeObj = {
      code: sizeCode?.toString() || null,
      description: sizeValue,
    };
  }

  // Transform contacts with WhatsApp enrichment
  const validContacts = transformContactsForEntity(searchData.contacts, enrichmentData);

  // Transform addresses with geolocation and proper addressMetadata
  const addresses = transformAddressesForEntity(searchData.addresses, enrichmentData);

  // Separate main activity from additional activities
  const activities = searchData.activities || [];
  const mainActivity = activities.find(a => a.isPrimary) || activities[0] || null;
  const additionalActivities = activities.filter(a => !a.isPrimary && a !== mainActivity);

  // Build dataEnrichment
  const dataEnrichment = buildDataEnrichment(searchData, enrichmentData);

  // Build the entity payload
  return {
    // Metadata - origin: 2 = automatic (created via dataSearch)
    _metadata: {
      origin: 2,
    },

    // Required fields
    uniqueKey,
    entityType: searchData.entityType || 'company',
    entityProfile,

    // Basic info
    name: searchData.name,
    fullName: searchData.fullName,
    foundedAt: searchData.foundedAt,
    legalNature: legalNatureObj,
    shareCapital: searchData.shareCapital,
    companySize: companySizeObj,
    segment: searchData.segment,

    // Arrays
    documents: searchData.documents || [],
    contacts: validContacts,
    addresses: addresses,
    holders: searchData.holders || [],

    // Activities (separated)
    mainActivity,
    additionalActivities,

    // Fiscal data
    fiscalData: searchData.fiscalData,

    // Registration status
    registrationStatus: searchData.registrationStatus,

    // Data enrichment status
    dataEnrichment,
  };
}

// *******************************************************************************
// SERVICE METHODS
// *******************************************************************************

/**
 * Perform a data search (CNPJ/CPF/Name/Phone/Email)
 * Calls backend endpoint which handles credit debit
 *
 * @param {Object} options - Search options
 * @param {string} options.engine - Search engine ('hod', 'chokmah', 'metatron')
 * @param {string} options.serviceType - Type of search ('cnpj', 'cpf', 'nome', 'phone', 'email')
 * @param {string} options.searchValue - The value to search for
 * @returns {Promise<Object>} Search result with entity data
 */
async function performSearch({ engine, serviceType, searchValue }) {
  if (!searchValue) {
    console.error('[dataSearchService] performSearch: searchValue is required');
    return { success: false, error: 'searchValue is required' };
  }

  // Clean value for CNPJ/CPF (remove non-digits)
  const cleanedValue =
    serviceType === 'cnpj' || serviceType === 'cpf'
      ? searchValue.replace(/\D/g, '')
      : searchValue;

  const [response] = await axiosService.post({
    url: API.DATA_SEARCH.SEARCH,
    data: {
      engine: engine || 'hod',
      serviceType: serviceType || 'cnpj',
      searchValue: cleanedValue,
    },
    returnAsObject: false,
  });

  // Backend returns array via routerService, get first element if needed
  const result = Array.isArray(response) ? response[0] : response;

  return result;
}

/**
 * Link a search result to an entity
 * Updates the dataSearch document with entityId, updatedKey, and syncStatus
 *
 * @param {Object} options - Options
 * @param {string} options._id - Data search document ID
 * @param {string} options.entityId - Entity ID to link
 * @param {string} [options.syncStatus='synced'] - Sync status ('synced', 'divergent', etc.)
 * @returns {Promise<Object>} Updated search document
 */
async function linkToEntity({ _id, entityId, syncStatus = 'synced' }) {
  if (!_id) {
    console.error('[dataSearchService] linkToEntity: _id is required');
    return { success: false, error: '_id is required' };
  }

  if (!entityId) {
    console.error('[dataSearchService] linkToEntity: entityId is required');
    return { success: false, error: 'entityId is required' };
  }

  const url = API.DATA_SEARCH.LINK.replace(':_id', _id);

  const [response] = await axiosService.post({
    url,
    data: { entityId, syncStatus },
    returnAsObject: false,
  });

  const result = Array.isArray(response) ? response[0] : response;
  return result;
}

/**
 * Update a search result (syncStatus, etc.)
 *
 * @param {Object} options - Options
 * @param {string} options._id - Data search document ID
 * @param {Object} options.data - Fields to update
 * @returns {Promise<Object>} Updated search document
 */
async function updateSearchResult({ _id, data }) {
  if (!_id) {
    console.error('[dataSearchService] updateSearchResult: _id is required');
    return { success: false, error: '_id is required' };
  }

  const url = API.DATA_SEARCH.URL.replace(':_id?', _id);

  const [response] = await axiosService.put({
    url,
    data,
    returnAsObject: false,
  });

  const result = Array.isArray(response) ? response[0] : response;
  return result;
}

/**
 * Get a search result by ID
 *
 * @param {string} _id - Data search document ID
 * @returns {Promise<Object>} Search document
 */
async function getById(_id) {
  if (!_id) {
    console.error('[dataSearchService] getById: _id is required');
    return { success: false, error: '_id is required' };
  }

  const url = API.DATA_SEARCH.URL.replace(':_id?', _id);

  const [response] = await axiosService.get({
    url,
    returnAsObject: false,
  });

  const result = Array.isArray(response) ? response[0] : response;
  return result;
}

/**
 * Find searches linked to an entity
 *
 * @param {string} entityId - Entity ID to search for
 * @returns {Promise<Array>} Array of search documents linked to the entity
 */
async function getByEntityId(entityId) {
  if (!entityId) {
    console.error('[dataSearchService] getByEntityId: entityId is required');
    return [];
  }

  // Use pagination endpoint with filter
  const filter = JSON.stringify({ entityId });
  const url = `${API.DATA_SEARCH.PAGINATION}?q=${encodeURIComponent(JSON.stringify({ filter: { entityId } }))}`;

  const [response] = await axiosService.get({
    url,
    returnAsObject: false,
  });

  const result = Array.isArray(response) ? response[0] : response;
  return result?.data || [];
}

// *******************************************************************************
// EXPORT
// *******************************************************************************

const dataSearchService = {
  // API methods
  performSearch,
  linkToEntity,
  updateSearchResult,
  getById,
  getByEntityId,
  // Transform helpers
  transformAddressesForEntity,
  transformContactsForEntity,
  buildDataEnrichment,
  buildEntityUpdatePayload,
  buildEntityCreatePayload,
};

export default dataSearchService;
